import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Brand palette (RGB) — matches the reporting UI
const CORAL = [0, 150, 137];
const DARK = [30, 41, 59];
const MED = [100, 116, 139];
const LIGHT = [148, 163, 184];
const STRIPE = [255, 245, 245];

const MARGIN = 40;
const HEADER_TOP = 56; // content top on continuation pages (below running header)

const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const sar = (v) => `${Number(v || 0).toLocaleString()} SAR`;
const dayName = (iso) => (iso || "").split("T")[0] || "-";

const NATURE_LABEL = { normal: "Normal", loyalty: "Loyalty", thirdParty: "Third Party", waiter: "Waiter" };

export const INVOICE_COLUMNS = [
  { key: "nature", label: "Order Type" },
  { key: "orders", label: "Orders" },
  { key: "sales", label: "Sales (pre-VAT)" },
  { key: "discount", label: "Discount (pre-VAT)" },
  { key: "net", label: "Net (pre-VAT)" },
  { key: "tax", label: "VAT (included)" },
  { key: "shipping", label: "Shipping" },
  { key: "total", label: "Total (paid)" },
  { key: "rate", label: "Rate %" },
  { key: "commission", label: "Commission" },
];

// Generate and download the StepPickup analytics report from the API payload.
// options (admin commission invoice): { showInvoice, invoiceRows, invoiceTotals, invoiceStatuses, hideScope }
export function generateStepPickupReport(data, options = {}) {
  const { showInvoice, invoiceRows, invoiceTotals, invoiceStatuses, hideScope } = options;
  const meta = data?.meta || {};
  const summary = data?.summary || {};
  const customers = data?.customers || {};
  const orders = data?.orders || {};
  const branches = data?.branches || {};
  const products = data?.products || {};

  const merchantName = meta.merchantName || "StepPickup";
  const reportTitle = `${merchantName} — App Orders Analytics`;
  // Prefer the merchant-local day labels — the ISO instants are tz-offset.
  const periodLabel =
    meta.period
      ? `${meta.period.startDateLocal || dayName(meta.period.startDate)}  to  ${meta.period.endDateLocal || dayName(meta.period.endDate)}`
      : "";
  const generatedOn = new Date().toLocaleString();

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Running header + footer drawn on every page by autoTable's didDrawPage hook.
  const decorate = () => {
    // top accent bar
    doc.setFillColor(...CORAL);
    doc.rect(0, 0, pageW, 4, "F");
    // running header text
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...LIGHT);
    doc.text(merchantName.toUpperCase(), MARGIN, 26);
    doc.text("ANALYTICS REPORT", pageW - MARGIN, 26, { align: "right" });
    // footer line
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN, pageH - 30, pageW - MARGIN, pageH - 30);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...LIGHT);
    doc.text(`Generated ${generatedOn}`, MARGIN, pageH - 18);
  };

  // ── Cover block (page 1) ──────────────────────────────
  let y = 90;
  doc.setFillColor(...CORAL);
  doc.rect(MARGIN, y - 26, 5, 34, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...DARK);
  doc.text(reportTitle, MARGIN + 14, y);

  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MED);
  if (periodLabel) {
    doc.text(`Period:  ${periodLabel}`, MARGIN + 14, y);
    y += 15;
  }
  const scopeBits = [
    meta.timezone ? `Timezone: ${meta.timezone}` : null,
    hideScope ? null : `Scope: app orders (${meta.orderScope || "orderNature='normal'"})`,
    `Success = ${meta.successDefinition || "not cancelled/returned"}`,
    `Churn window: ${meta.churnDays ?? 60} days`,
  ].filter(Boolean);
  doc.setFontSize(8.5);
  doc.setTextColor(...LIGHT);
  scopeBits.forEach((b) => {
    doc.text(b, MARGIN + 14, y);
    y += 12;
  });
  y += 12;

  // Shared autoTable styling + section helper.
  const tableDefaults = {
    margin: { left: MARGIN, right: MARGIN, top: HEADER_TOP, bottom: 40 },
    styles: { font: "helvetica", fontSize: 9, cellPadding: 5, textColor: DARK, lineColor: [226, 232, 240], lineWidth: 0.5 },
    headStyles: { fillColor: CORAL, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
    alternateRowStyles: { fillColor: STRIPE },
    didDrawPage: decorate,
  };

  const section = (title, head, body, columnStyles) => {
    if (!body || body.length === 0) return;
    // Section heading; push to a new page if too close to the bottom.
    if (y > pageH - 90) {
      doc.addPage();
      y = HEADER_TOP;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(...DARK);
    doc.text(title, MARGIN, y);
    autoTable(doc, {
      ...tableDefaults,
      startY: y + 8,
      head: [head],
      body,
      ...(columnStyles ? { columnStyles } : {}),
    });
    y = doc.lastAutoTable.finalY + 26;
  };

  // ── 1. Key Metrics ────────────────────────────────────
  section("Key Metrics", ["Metric", "Value"], [
    ["Total orders", dash(summary.totalOrders)],
    ["Successful orders", dash(summary.successOrders)],
    ["Cancelled orders", `${dash(summary.cancelledOrders)}  (${summary.cancellationRate ?? 0}%)`],
    ["Returned orders", dash(summary.returnedOrders)],
    ["Total revenue", sar(summary.totalRevenue)],
    ["Average order value", sar(summary.avgOrderValue)],
    ["Paid / Unpaid", `${dash(summary.paidOrders)} / ${dash(summary.unpaidOrders)}`],
    ["Active customers (period)", dash(summary.activeCustomers)],
    ["New customers", dash(summary.newCustomers)],
    ["Returning customers", dash(summary.returningCustomers)],
  ], { 0: { fontStyle: "bold", cellWidth: 220 } });

  // ── 2. Customer Lifecycle ─────────────────────────────
  section("Customer Lifecycle (all-time)", ["Metric", "Value"], [
    ["Lifetime customers", dash(customers.lifetimeCustomers)],
    ["Repeating customers (2+ orders)", `${dash(customers.repeatingCustomers)}  (${customers.repeatRate ?? 0}% repeat rate)`],
    ["One-time customers", dash(customers.oneTimeCustomers)],
    [`Not returned (no order in ${meta.churnDays ?? 60} days)`, dash(customers.notReturnedCustomers)],
    ["Favourite product", dash(products.favouriteProduct)],
    ["Favourite branch", dash(branches.favouriteBranch)],
  ], { 0: { fontStyle: "bold", cellWidth: 260 } });

  // ── 3. New customers per month ────────────────────────
  section("New Customers per Month", ["Month", "New customers"],
    (customers.newCustomersPerMonth || []).map((r) => [r.period, r.count]));

  // ── 4. Orders per month / week ────────────────────────
  section("Orders per Month", ["Month", "Orders"],
    (orders.ordersPerMonth || []).map((r) => [r.period, r.count]));

  section("Orders per Week", ["ISO Week", "Orders"],
    (orders.ordersPerWeek || []).map((r) => [r.period, r.count]));

  // ── 5. Orders by day of week & hour ───────────────────
  section("Orders by Day of Week", ["Day", "Orders"],
    (orders.ordersByDayOfWeek || []).map((r) => [r.day, r.count]));

  const hourRows = (orders.ordersByHour || [])
    .filter((r) => r.count > 0)
    .map((r) => [`${r.hour}:00 - ${r.hour}:59`, r.count]);
  section("Peak Hours (hours with orders)", ["Hour", "Orders"], hourRows);

  // ── 6. Status & type breakdown ────────────────────────
  section("Order Status Breakdown", ["Status", "Orders"],
    (orders.statusBreakdown || []).map((r) => [r.status, r.count]));

  section("Order Type Breakdown", ["Type", "Orders"],
    (orders.orderTypeBreakdown || []).map((r) => [r.type, r.count]));

  // ── 7. Branch performance ─────────────────────────────
  section("Branch Performance",
    ["Branch", "Orders", "Success", "Cancelled", "Cancel %", "Revenue"],
    (branches.performance || []).map((b) => [
      b.branch, b.orders, b.successOrders, b.cancelledOrders, `${b.cancellationRate}%`, sar(b.revenue),
    ]),
    { 0: { fontStyle: "bold" }, 5: { halign: "right" } });

  // ── 8. Products ───────────────────────────────────────
  section("Top Products", ["#", "Product", "Qty sold", "Revenue"],
    (products.top || []).map((p, i) => [i + 1, p.title, p.qtySold, sar(p.revenue)]),
    { 0: { cellWidth: 28 }, 3: { halign: "right" } });

  section("Revenue by Category", ["Category", "Qty sold", "Revenue"],
    (products.categories || []).map((c) => [c.category, c.qtySold, sar(c.revenue)]),
    { 2: { halign: "right" } });

  // ── 9. Commission Invoice (admin only) ────────────────
  if (showInvoice && Array.isArray(invoiceRows) && invoiceRows.length) {
    if (y > pageH - 120) { doc.addPage(); y = HEADER_TOP; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(...DARK);
    doc.text("Commission Invoice", MARGIN, y);
    if (invoiceStatuses && invoiceStatuses.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...MED);
      doc.text(`Billable statuses: ${invoiceStatuses.join(", ")}`, MARGIN, y + 13);
    }
    const t = invoiceTotals || {};
    // One cell per INVOICE_COLUMNS entry, in order.
    const invoiceCell = (r, key) => {
      if (key === "nature") return NATURE_LABEL[r.nature] || r.nature;
      if (key === "orders") return r.orders;
      if (key === "rate") return `${r.rate}%`;
      return sar(r[key]);
    };
    autoTable(doc, {
      ...tableDefaults,
      startY: y + 20,
      head: [INVOICE_COLUMNS.map((c) => c.label)],
      body: invoiceRows.map((r) => INVOICE_COLUMNS.map((c) => invoiceCell(r, c.key))),
      foot: [INVOICE_COLUMNS.map((c) => {
        if (c.key === "nature") return "Grand Total";
        if (c.key === "orders") return t.orders ?? "";
        if (c.key === "rate") return "";
        return sar(t[c.key]);
      })],
      showFoot: "lastPage",
      styles: { ...tableDefaults.styles, fontSize: 7, cellPadding: 3.5 },
      headStyles: { ...tableDefaults.headStyles, fontSize: 7 },
      footStyles: { fillColor: [224, 242, 241], textColor: DARK, fontStyle: "bold", fontSize: 7 },
      // Right-align every column after "Order Type".
      columnStyles: Object.fromEntries(
        INVOICE_COLUMNS.map((c, i) => [i, i === 0 ? {} : { halign: "right" }])
      ),
    });
    y = doc.lastAutoTable.finalY + 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...MED);
    doc.text(
      "Amounts exclude VAT except “VAT (included)” (tax contained in Total) and “Total (paid)” (incl. VAT & shipping).",
      MARGIN, y
    );
    doc.text(
      "Commission = Rate % × Net (pre-VAT sales − pre-VAT discounts; shipping excluded).",
      MARGIN, y + 11
    );
    y += 37;
  }

  // ── Page numbers (after all pages exist) ──────────────
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...LIGHT);
    doc.text(`Page ${i} of ${total}`, pageW - MARGIN, pageH - 18, { align: "right" });
  }

  const fileLabel = merchantName.replace(/\s+/g, "-").toLowerCase();
  const stamp = new Date().toISOString().split("T")[0];
  doc.save(`steppickup-analytics-${fileLabel}-${stamp}.pdf`);
}
